import { useState } from 'react';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Download, Trash2, Edit2, FileText, Users, DollarSign, Clock,
  FileCheck, TrendingUp, Building2, Wallet, AlertCircle, Printer, Mail
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingTaxSlips({ t4aSummary, taxSlipsData, t4aAgents, fiscalYear, shareholderAllocations, loadData }) {
  const [showDividendModal, setShowDividendModal] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [dividendForm, setDividendForm] = useState({
    agent_id: '', agent_name: '', total_earnings: 0, dividend_amount: 0, commission_amount: 0, notes: ''
  });

  const downloadT4aSlip = async (agentId, agentName) => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/accounting/t4a/generate/${agentId}?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4A_${agentName.replace(/\s+/g, '_')}_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`T4A slip downloaded for ${agentName}`);
    } catch (err) { toast.error('Failed to download T4A slip'); }
  };

  const downloadT4Slip = async (agentId, agentName) => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/accounting/t4/generate/${agentId}?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4_${agentName.replace(/\s+/g, '_')}_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`T4 slip downloaded for ${agentName}`);
    } catch (err) { toast.error('Failed to download T4 slip'); }
  };

  const emailT4aSlip = async (agentId, agentName, agentEmail) => {
    if (!agentEmail) { toast.error(`${agentName} does not have an email address on file`); return; }
    const token = getToken();
    try {
      toast.loading(`Sending T4A to ${agentEmail}...`, { id: 'email-t4a' });
      await axios.post(`${API}/api/accounting/t4a/email/${agentId}?fiscal_year=${fiscalYear}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`T4A slip sent to ${agentEmail}`, { id: 'email-t4a' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email', { id: 'email-t4a' }); }
  };

  const downloadT4aSummary = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/accounting/t4a/summary/pdf?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4A_Summary_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('T4A Summary downloaded');
    } catch (err) { toast.error('Failed to download T4A Summary'); }
  };

  const downloadAllT4aSlips = async () => {
    toast.info('Downloading all T4A slips...');
    for (const agent of t4aAgents) {
      await downloadT4aSlip(agent.id, agent.name);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    toast.success(`Downloaded ${t4aAgents.length} T4A slips`);
  };

  const emailAllT4aSlips = async () => {
    const token = getToken();
    const agentsWithEmail = t4aAgents.filter(a => a.email);
    if (agentsWithEmail.length === 0) { toast.error('No agents have email addresses on file'); return; }
    toast.loading(`Sending T4A slips to ${agentsWithEmail.length} agents...`, { id: 'email-all-t4a' });
    let successCount = 0, failCount = 0;
    for (const agent of agentsWithEmail) {
      try {
        await axios.post(`${API}/api/accounting/t4a/email/${agent.id}?fiscal_year=${fiscalYear}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        successCount++;
      } catch (err) { failCount++; }
      await new Promise(resolve => setTimeout(resolve, 800));
    }
    if (failCount === 0) toast.success(`Successfully sent T4A slips to ${successCount} agents`, { id: 'email-all-t4a' });
    else toast.warning(`Sent ${successCount} emails, ${failCount} failed`, { id: 'email-all-t4a' });
  };

  const openDividendModal = (agent) => {
    const contractor = taxSlipsData.contractors?.find(c => c.id === agent.id) || agent;
    const totalEarnings = contractor.total_commission || 0;
    const existingAlloc = shareholderAllocations.find(a => a.agent_id === agent.id);
    setDividendForm({
      agent_id: agent.id, agent_name: agent.name, total_earnings: totalEarnings,
      dividend_amount: existingAlloc?.dividend_amount || 0,
      commission_amount: existingAlloc?.commission_amount || totalEarnings,
      notes: existingAlloc?.notes || ''
    });
    setEditingAllocation(existingAlloc || null);
    setShowDividendModal(true);
  };

  const saveDividendAllocation = async () => {
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/shareholders/allocations`, {
        ...dividendForm, fiscal_year: parseInt(fiscalYear),
        total_earnings: parseFloat(dividendForm.total_earnings) || 0,
        dividend_amount: parseFloat(dividendForm.dividend_amount) || 0,
        commission_amount: parseFloat(dividendForm.commission_amount) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Dividend allocation saved for ${dividendForm.agent_name}`);
      setShowDividendModal(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save allocation'); }
  };

  const deleteDividendAllocation = async (agentId, agentName) => {
    if (!confirm(`Delete dividend allocation for ${agentName}?`)) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/shareholders/allocations/${agentId}?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Allocation deleted');
      loadData();
    } catch (err) { toast.error('Failed to delete allocation'); }
  };

  const downloadT5Slip = async (agentId, agentName) => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/accounting/t5/generate/${agentId}?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T5_${agentName.replace(/\s+/g, '_')}_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`T5 slip downloaded for ${agentName}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to download T5 slip'); }
  };

  const emailT5Slip = async (agentId, agentName, agentEmail) => {
    if (!agentEmail) { toast.error(`${agentName} does not have an email address on file`); return; }
    const token = getToken();
    try {
      toast.loading(`Sending T5 to ${agentEmail}...`, { id: 'email-t5' });
      await axios.post(`${API}/api/accounting/t5/email/${agentId}?fiscal_year=${fiscalYear}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`T5 slip sent to ${agentEmail}`, { id: 'email-t5' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send T5 email', { id: 'email-t5' }); }
  };

  const renderAgentRow = (agent, idx, isEmployee) => {
    const isBothDivisions = agent.division === 'both' || (agent.mortgage_commissions > 0 && agent.insurance_commissions > 0);
    return (
      <TableRow key={agent.id} className={isBothDivisions ? 'bg-gradient-to-r from-emerald-50/50 to-transparent' : ''}>
        <TableCell className="font-mono text-slate-500">{idx + 1}</TableCell>
        <TableCell>
          <div>
            <p className="font-medium">{agent.name}</p>
            {agent.email && <p className="text-xs text-slate-500">{agent.email}</p>}
            {isBothDivisions && <Badge variant="outline" className="mt-1 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Both Divisions</Badge>}
          </div>
        </TableCell>
        <TableCell>
          {isBothDivisions ? (
            <div className="space-y-1">
              <div className="flex items-center gap-1"><Badge className="bg-blue-100 text-blue-800 text-xs">Mortgage</Badge><span className="text-xs font-mono text-blue-700">{formatCurrency(agent.mortgage_commissions || 0)}</span></div>
              <div className="flex items-center gap-1"><Badge className="bg-purple-100 text-purple-800 text-xs">Insurance</Badge><span className="text-xs font-mono text-purple-700">{formatCurrency(agent.insurance_commissions || 0)}</span></div>
            </div>
          ) : (
            <Badge className={agent.division === 'mortgage' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>{agent.division}</Badge>
          )}
        </TableCell>
        <TableCell className="text-right">
          <span className={`font-mono font-bold ${isBothDivisions ? 'text-emerald-600 text-lg' : isEmployee ? 'text-blue-600' : 'text-amber-600'}`}>{formatCurrency(agent.total_commission)}</span>
          {isBothDivisions && <p className="text-xs text-slate-500">Combined</p>}
        </TableCell>
        <TableCell className="text-center"><Badge variant="outline" className={isBothDivisions ? 'bg-emerald-50' : ''}>{agent.commission_count}</Badge></TableCell>
        <TableCell className="text-right">
          <div className="flex gap-1 justify-end">
            {isEmployee ? (
              <Button variant="outline" size="sm" onClick={() => downloadT4Slip(agent.id, agent.name)} className={isBothDivisions ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700' : 'bg-blue-50 hover:bg-blue-100 text-blue-700'} data-testid={`download-t4-${agent.id}`}>
                <Download className="w-4 h-4 mr-1" /> T4
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => downloadT4aSlip(agent.id, agent.name)} className={isBothDivisions ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'} data-testid={`download-t4a-${agent.id}`}>
                  <Download className="w-4 h-4 mr-1" /> T4A
                </Button>
                <Button variant="outline" size="sm" onClick={() => emailT4aSlip(agent.id, agent.name, agent.email)} className="bg-blue-50 hover:bg-blue-100 text-blue-700" data-testid={`email-t4a-${agent.id}`} title={agent.email ? `Send to ${agent.email}` : 'No email on file'}>
                  <Mail className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => openDividendModal(agent)}
                  className={shareholderAllocations.find(a => a.agent_id === agent.id) ? 'bg-green-100 hover:bg-green-200 text-green-700 border-green-300' : 'bg-slate-50 hover:bg-slate-100 text-slate-600'}
                  data-testid={`dividend-${agent.id}`} title="Configure dividend allocation (T5)">
                  <TrendingUp className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      {/* Summary Cards */}
      {t4aSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><Users className="w-6 h-6 text-blue-600" /></div><div><p className="text-xs text-slate-500 uppercase">T4 (Employees)</p><p className="text-xl font-bold text-blue-600">{taxSlipsData.summary?.total_t4 || 0}</p></div></div></CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center"><Users className="w-6 h-6 text-amber-600" /></div><div><p className="text-xs text-slate-500 uppercase">T4A (Contractors)</p><p className="text-xl font-bold text-amber-600">{taxSlipsData.summary?.total_t4a || t4aSummary.summary?.total_t4a_slips || 0}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><DollarSign className="w-6 h-6 text-emerald-600" /></div><div><p className="text-xs text-slate-500 uppercase">Total Commissions</p><p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency((taxSlipsData.summary?.total_t4_amount || 0) + (taxSlipsData.summary?.total_t4a_amount || t4aSummary.summary?.total_box_020 || 0))}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center"><Clock className="w-6 h-6 text-red-600" /></div><div><p className="text-xs text-slate-500 uppercase">Filing Deadline</p><p className="text-lg font-bold text-red-600">{t4aSummary.summary?.filing_deadline}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center"><FileCheck className="w-6 h-6 text-purple-600" /></div><div><p className="text-xs text-slate-500 uppercase">Tax Year</p><p className="text-xl font-bold text-purple-600">{fiscalYear}</p></div></div></CardContent></Card>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={downloadT4aSummary} data-testid="download-t4a-summary-btn"><Download className="w-4 h-4 mr-2" /> Download T4/T4A Summary (CRA)</Button>
        <Button variant="outline" onClick={downloadAllT4aSlips} disabled={t4aAgents.length === 0}><Printer className="w-4 h-4 mr-2" /> Download All Slips ({t4aAgents.length})</Button>
        <Button variant="outline" onClick={emailAllT4aSlips} disabled={t4aAgents.filter(a => a.email).length === 0} className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200" data-testid="email-all-t4a-btn">
          <Mail className="w-4 h-4 mr-2" /> Email All T4A Slips ({t4aAgents.filter(a => a.email).length})
        </Button>
      </div>

      {/* Division Breakdown */}
      {t4aSummary && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" /> Mortgage Division</CardTitle></CardHeader>
            <CardContent><div className="space-y-2"><div className="flex justify-between"><span className="text-slate-600">T4 Slips (Employees)</span><span className="font-bold">{t4aSummary.by_division?.mortgage?.t4_count || 0}</span></div><div className="flex justify-between"><span className="text-slate-600">T4A Slips (Contractors)</span><span className="font-bold">{t4aSummary.by_division?.mortgage?.t4a_count || t4aSummary.by_division?.mortgage?.count || 0}</span></div><div className="flex justify-between border-t pt-2"><span className="text-slate-600 font-medium">Total Commissions</span><span className="font-mono font-bold text-emerald-600">{formatCurrency(t4aSummary.by_division?.mortgage?.total || 0)}</span></div></div></CardContent></Card>
          <Card><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Wallet className="w-5 h-5 text-purple-600" /> Insurance & Investment Division</CardTitle></CardHeader>
            <CardContent><div className="space-y-2"><div className="flex justify-between"><span className="text-slate-600">T4 Slips (Employees)</span><span className="font-bold">{t4aSummary.by_division?.insurance?.t4_count || 0}</span></div><div className="flex justify-between"><span className="text-slate-600">T4A Slips (Contractors)</span><span className="font-bold">{t4aSummary.by_division?.insurance?.t4a_count || t4aSummary.by_division?.insurance?.count || 0}</span></div><div className="flex justify-between border-t pt-2"><span className="text-slate-600 font-medium">Total Commissions</span><span className="font-mono font-bold text-emerald-600">{formatCurrency(t4aSummary.by_division?.insurance?.total || 0)}</span></div></div></CardContent></Card>
        </div>
      )}

      {/* T4 Employees */}
      <Card>
        <CardHeader className="bg-blue-50 border-b border-blue-100">
          <CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> T4 Slips - Employees</CardTitle>
          <CardDescription>T4 slips for employees with employment income, CPP/EI deductions, and tax withheld</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {(taxSlipsData.employees?.length || 0) === 0 ? (
            <div className="text-center py-6 text-slate-500"><Users className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No employees with commissions found for {fiscalYear}</p><p className="text-sm">Set agent employment type to &quot;Employee&quot; in Agents page to generate T4</p></div>
          ) : (
            <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>#</TableHead><TableHead>Agent Name</TableHead><TableHead>Division</TableHead><TableHead className="text-right">Box 14 (Employment Income)</TableHead><TableHead className="text-center">Payments</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{taxSlipsData.employees?.map((agent, idx) => renderAgentRow(agent, idx, true))}</TableBody></Table></div>
          )}
        </CardContent>
      </Card>

      {/* T4A Contractors */}
      <Card>
        <CardHeader className="bg-amber-50 border-b border-amber-100">
          <CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-amber-600" /> T4A Slips - Contractors</CardTitle>
          <CardDescription>T4A slips for self-employed contractors with Box 020 (Commissions)</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {(taxSlipsData.contractors?.length || t4aAgents.filter(a => a.employment_type !== 'employee').length || 0) === 0 ? (
            <div className="text-center py-6 text-slate-500"><Users className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No contractors with commissions found for {fiscalYear}</p></div>
          ) : (
            <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>#</TableHead><TableHead>Agent Name</TableHead><TableHead>Division</TableHead><TableHead className="text-right">Box 020 (Commissions)</TableHead><TableHead className="text-center">Payments</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{(taxSlipsData.contractors?.length > 0 ? taxSlipsData.contractors : t4aAgents.filter(a => a.employment_type !== 'employee')).map((agent, idx) => renderAgentRow(agent, idx, false))}</TableBody></Table></div>
          )}
        </CardContent>
      </Card>

      {/* Shareholder Allocations */}
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="bg-green-100 border-b border-green-200">
          <CardTitle className="text-lg flex items-center gap-2 text-green-800"><TrendingUp className="w-5 h-5 text-green-600" /> Shareholder Dividend Allocations (T5)</CardTitle>
          <CardDescription className="text-green-700">Configure how owner/shareholder income is split between commissions (T4A) and dividends (T5)</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {shareholderAllocations.length === 0 ? (
            <div className="text-center py-6 text-slate-500"><AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No dividend allocations configured for {fiscalYear}</p><p className="text-sm mt-1">Click on a contractor below to set up dividend allocation</p></div>
          ) : (
            <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Shareholder</TableHead><TableHead className="text-right">Total Earnings</TableHead><TableHead className="text-right">Commission (T4A)</TableHead><TableHead className="text-right">Dividend (T5)</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {shareholderAllocations.map((alloc) => (
                  <TableRow key={alloc.agent_id} className="bg-green-50/50">
                    <TableCell><div><p className="font-medium">{alloc.agent_name}</p><Badge className="bg-green-100 text-green-800 text-xs">Shareholder</Badge></div></TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(alloc.total_earnings)}</TableCell>
                    <TableCell className="text-right"><span className="font-mono text-amber-600">{formatCurrency(alloc.commission_amount)}</span>{alloc.commission_amount > 0 && <Badge className="ml-1 bg-amber-100 text-amber-800 text-xs">T4A</Badge>}</TableCell>
                    <TableCell className="text-right"><span className="font-mono text-green-600 font-bold">{formatCurrency(alloc.dividend_amount)}</span><Badge className="ml-1 bg-green-100 text-green-800 text-xs">T5</Badge></TableCell>
                    <TableCell className="max-w-[150px] truncate text-sm text-slate-600">{alloc.notes || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="outline" size="sm" onClick={() => downloadT5Slip(alloc.agent_id, alloc.agent_name)} className="bg-green-50 hover:bg-green-100 text-green-700" data-testid={`download-t5-${alloc.agent_id}`}><Download className="w-4 h-4 mr-1" /> T5</Button>
                        <Button variant="outline" size="sm" onClick={() => openDividendModal({ id: alloc.agent_id, name: alloc.agent_name, total_commission: alloc.total_earnings })} className="bg-slate-50 hover:bg-slate-100" data-testid={`edit-dividend-${alloc.agent_id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => deleteDividendAllocation(alloc.agent_id, alloc.agent_name)} className="bg-red-50 hover:bg-red-100 text-red-700" data-testid={`delete-dividend-${alloc.agent_id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody></Table></div>
          )}
          <div className="mt-4 pt-4 border-t border-green-200">
            <p className="text-sm text-green-700 mb-2"><strong>To add dividend allocation:</strong> Find the shareholder in the T4A Contractors list above and click the <TrendingUp className="w-4 h-4 inline" /> button.</p>
          </div>
        </CardContent>
      </Card>

      {/* CRA Filing Info */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-amber-800 mb-2">CRA Filing Information</h4>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• <strong>T4A Slip:</strong> Statement of Pension, Retirement, Annuity, and Other Income</li>
            <li>• <strong>Box 020:</strong> Self-employed commissions paid to agents</li>
            <li>• <strong>Filing Deadline:</strong> Last day of February following the tax year</li>
            <li>• <strong>Note:</strong> T4A slips must be provided to recipients by the same deadline</li>
            <li>• <strong>Important:</strong> Verify all agent SIN numbers before official CRA submission</li>
          </ul>
        </CardContent>
      </Card>

      {/* Dividend Modal */}
      <Dialog open={showDividendModal} onOpenChange={setShowDividendModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700"><TrendingUp className="w-5 h-5" /> Shareholder Dividend Allocation</DialogTitle>
            <DialogDescription>Configure how {dividendForm.agent_name}'s income is split between commission (T4A) and dividend (T5)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-slate-700">Shareholder: {dividendForm.agent_name}</p>
              <p className="text-lg font-bold text-slate-900">Total Earnings: {formatCurrency(dividendForm.total_earnings)}</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-green-700">Dividend Amount (T5 - Box 24)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">$</span>
                  <Input type="number" value={dividendForm.dividend_amount} onChange={(e) => { const dividend = parseFloat(e.target.value) || 0; setDividendForm(prev => ({ ...prev, dividend_amount: dividend, commission_amount: prev.total_earnings - dividend })); }} min="0" max={dividendForm.total_earnings} step="0.01" className="border-green-200 focus:border-green-400" />
                </div>
                <p className="text-xs text-green-600 mt-1">Amount to be reported as eligible dividends on T5 slip</p>
              </div>
              <div>
                <Label className="text-amber-700">Commission Amount (T4A - Box 020)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">$</span>
                  <Input type="number" value={dividendForm.commission_amount} onChange={(e) => { const commission = parseFloat(e.target.value) || 0; setDividendForm(prev => ({ ...prev, commission_amount: commission, dividend_amount: prev.total_earnings - commission })); }} min="0" max={dividendForm.total_earnings} step="0.01" className="border-amber-200 focus:border-amber-400" />
                </div>
                <p className="text-xs text-amber-600 mt-1">Amount to be reported as self-employment commissions on T4A slip</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex justify-between text-sm"><span>Dividend (T5):</span><span className="font-mono text-green-700">{formatCurrency(dividendForm.dividend_amount)}</span></div>
                <div className="flex justify-between text-sm"><span>Commission (T4A):</span><span className="font-mono text-amber-700">{formatCurrency(dividendForm.commission_amount)}</span></div>
                <div className="flex justify-between text-sm font-bold border-t border-blue-200 pt-1 mt-1">
                  <span>Total:</span>
                  <span className={`font-mono ${Math.abs(dividendForm.dividend_amount + dividendForm.commission_amount - dividendForm.total_earnings) < 0.01 ? 'text-green-700' : 'text-red-600'}`}>{formatCurrency(dividendForm.dividend_amount + dividendForm.commission_amount)}</span>
                </div>
                {Math.abs(dividendForm.dividend_amount + dividendForm.commission_amount - dividendForm.total_earnings) >= 0.01 && (
                  <p className="text-red-600 text-xs mt-2">Warning: Total must equal {formatCurrency(dividendForm.total_earnings)}</p>
                )}
              </div>
              <div><Label>Notes (optional)</Label><Input value={dividendForm.notes} onChange={(e) => setDividendForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="e.g., Board resolution dated Jan 15, 2025" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDividendModal(false)}>Cancel</Button>
            <Button onClick={saveDividendAllocation} className="bg-green-600 hover:bg-green-700" disabled={Math.abs(dividendForm.dividend_amount + dividendForm.commission_amount - dividendForm.total_earnings) >= 0.01}>Save Allocation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
